import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  INDICATOR_ANALYSIS_SUMMARY,
  PAGE_SIZE_OPTIONS,
} from "../../../utils/constants";
import AnalysisFilterTemplate from "../component/AnalysisFilterTemplate";
import { useFilter } from "../../../hooks/filter/useFilter";
import { Option } from "../../../types/General/Option";
import SelectGroup from "../../../components/molecule/SelectGroup/SelectGroup";
import { getIndicatorLevel } from "../../../services/Indicator/getIndicatorLevel";
import { indicatorAnalysisSelectFilterData } from "../data/Indicator/indicatorAnalysisSelectFilterData";
import IndicatorSearchTable from "./IndicatorSearchTable";
import { Box } from "@chakra-ui/react";
import { borderRadius, borderStyle } from "../../../utils/styles/styles";
import useFetchData from "../../../hooks/data/useFetchData";
import { getIndicator } from "../../../services/Indicator/getIndicator";
import { getIndicatorAnalytics } from "../../../services/Indicator/getIndicatorAnalytics";
import { PaginationValues } from "../../../types/General/PaginationValues";
import { getCountyById } from "../../../services/TabelasAuxiliares/getCountyById";
import { getProvince } from "../../../services/TabelasAuxiliares/getProvince";
import { updateSelectedFilterItemsName } from "../../../helpers/updateSelectedFilterItemsName";
import { indicatorTableSelectFilterData } from "../data/Indicator/indicatorTableSelectFilterData";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import TableIndicator from "./TableIndicator";
import useTableRendering from "../../../hooks/table/useTableRendering";
import { PaginationConfig } from "../../../types/Components/Table/PaginationConfig";
import { FilterConfig } from "../../../types/Components/Table/FilterConfig";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import { useCustomModal } from "../../../hooks/modal/useCustomModal";
import { Indicator } from "../../../types/Indicator/Indicator";
import { getIndicatorDataStates } from "../../../services/Indicator/getIndicatorDataStates";
import { renderLoadingState } from "../../../hooks/table/TableStates";
import { useAuth } from "../../../provider/AuthProvider";
import { applyRoleBasedFilters } from "../../../helpers/Filter/authUserFilter";
import { IndicatorFormValues } from "../../../types/Indicator/IndicatorDataReport/IndicatorFormValues";

interface FilterState {
  IndicatorId: Indicator | null;
  IndicatorLevelId: Option | null;
  ProvinceId: Option | null;
  CountyId: Option | null;
  StateId: Option | null;
}

interface OptionsState {
  indicatorOptions: Indicator[];
  levelOptions: Option[];
  provinceOptions: Option[];
  countyOptions: Option[];
  stateOptions: Option[];
}

const IndicatorAnalysis: FC = () => {
  const { auth } = useAuth();
  const user = auth.user;
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [selectedFilterItems, setSelectedFilterItems] = useState<string>("");
  const [totalItems, setTotalItems] = useState<number>(0);
  const [showTable, setShowTable] = useState<boolean>(false);
  const [countyLoaded, setCountyLoaded] = useState<boolean>(false);
  const [analysisLoaded, setAnalysisLoaded] = useState(false);
  const [formValues, setFormValues] = useState<IndicatorFormValues>({
    frequency: null,
    province: null,
    county: null,
    indicator: null,
  });

  const [filterState, setFilterState] = useState<FilterState>({
    IndicatorId: null,
    IndicatorLevelId:
      user?.role?.level?.id === 4
        ? { id: 4, name: "Provincial" }
        : user?.role?.level?.id === 5
          ? { id: 3, name: "Municipal" }
          : null,
    ProvinceId:
      user?.role?.level?.id >= 4 && user?.province
        ? { id: user.province.id, name: user.province.name }
        : null,
    CountyId:
      user?.role?.level?.id === 5 && user?.county
        ? { id: user.county.id, name: user.county.name }
        : null,
    StateId: null,
  });
  const [optionsState, setOptionsState] = useState<OptionsState>({
    indicatorOptions: [],
    levelOptions: [],
    provinceOptions: [],
    countyOptions: [],
    stateOptions: [],
  });

  const { control, setValue, resetField } = useForm();
  const filterNames = useMemo(
    () => [
      "IndicatorId",
      "IndicatorLevelId",
      "ProvinceId",
      "CountyId",
      "StateId",
    ],
    [],
  );

  const { filters, setFilter } = useFilter({ setValue, filterNames });

  useEffect(() => {
    const fetchData = (
      filterValues?: any,
      paginationValues?: PaginationValues,
    ) => {
      setErrorFindingData(false);
      const updatedFilters = applyRoleBasedFilters(
        user,
        filterValues,
        setFilter,
        true,
      );
      getIndicatorAnalytics(updatedFilters, paginationValues)
        .then((result) => {
          setDataTable(result.content);
          setTotalItems(result.totalItems);
          filterState.IndicatorLevelId || filterState.IndicatorId
            ? setIsLoaded(true)
            : setIsLoaded(false);
          setIsSearching(false);
        })
        .catch((error) => {
          console.log("API Error:", error);
          setErrorFindingData(true);
          setIsSearching(false);
        });
      setTimeout(() => {
        setAnalysisLoaded(true);
      }, 1000);
    };
    isSearching &&
      fetchData(filters, {
        CurrentPage: currentPage,
        PageSize: pageSize,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  useEffect(() => {
    setShowTable(false);
  }, [filterState.IndicatorLevelId, filterState.IndicatorId]);

  useEffect(() => {
    const updateCountyOptions = () => {
      setCountyLoaded(false);
      if (filterState.ProvinceId) {
        getCountyById({ ParentId: filterState.ProvinceId.id })
          .then((result) => {
            setOptionsState((prev) => ({
              ...prev,
              countyOptions: result,
            }));
          })
          .finally(() => {
            setCountyLoaded(true);
          });
      } else {
        setCountyLoaded(true);
        setOptionsState((prev) => ({ ...prev, countyOptions: [] }));
      }
    };
    updateCountyOptions();
  }, [filterState.ProvinceId]);

  const { isLoaded: levelLoaded } = useFetchData(getIndicatorLevel, (data) =>
    setOptionsState((prev) => ({ ...prev, levelOptions: data })),
  );
  const { isLoaded: indicatorLoaded } = useFetchData(getIndicator, (data) =>
    setOptionsState((prev) => ({ ...prev, indicatorOptions: data })),
  );
  const { isLoaded: provinceLoaded } = useFetchData(getProvince, (data) =>
    setOptionsState((prev) => ({ ...prev, provinceOptions: data })),
  );
  const { isLoaded: stateLoaded } = useFetchData(
    getIndicatorDataStates,
    (data) => setOptionsState((prev) => ({ ...prev, stateOptions: data })),
  );

  const filtersArray = filterNames
    .map((filterName) => ({
      key: filterName,
      value: filters[filterName],
      setValue: (value: string | undefined) => setFilter(filterName, value),
    }))
    .filter((filter) => filter.value !== undefined);

  const keepActiveFiltersArray = filtersArray.filter((filter) =>
    ["IndicatorLevelId", "IndicatorId"].includes(filter.key),
  );

  const selectComponentsMainTable = indicatorAnalysisSelectFilterData(
    optionsState.indicatorOptions,
    optionsState.levelOptions,
    control,
    setFilter,
    (IndicatorId: any) =>
      setFilterState((prev) => ({
        ...prev,
        IndicatorId: IndicatorId,
      })),
    (IndicatorLevelId: any) =>
      setFilterState((prev) => ({
        ...prev,
        IndicatorLevelId: IndicatorLevelId,
      })),
    filterState.IndicatorId,
    indicatorLoaded,
    levelLoaded,
    user,
  );

  const filterChildrenMainTable = (
    <SelectGroup
      selectComponents={selectComponentsMainTable}
      spaceBetween={2}
      flexSize={1}
    />
  );

  useEffect(() => {
    updateSelectedFilterItemsName(
      filterState,
      filters,
      setSelectedFilterItems,
      filterNames.filter(
        (filterName) =>
          !["IndicatorId", "IndicatorLevelId"].includes(filterName),
      ),
    );
  }, [filterNames, filterState, filters]);

  const selectComponents = indicatorTableSelectFilterData(
    optionsState.stateOptions,
    optionsState.provinceOptions,
    optionsState.countyOptions,
    control,
    setFilter,
    (StateId: any) =>
      setFilterState((prev) => ({
        ...prev,
        StateId: StateId,
      })),
    (ProvinceId: any) =>
      setFilterState((prev) => ({ ...prev, ProvinceId: ProvinceId })),
    (CountyId: any) =>
      setFilterState((prev) => ({ ...prev, CountyId: CountyId })),
    filterState.ProvinceId,
    filterState.IndicatorLevelId,
    provinceLoaded,
    countyLoaded,
    stateLoaded,
    user,
  );

  const filterChildren = (
    <SelectGroup selectComponents={selectComponents} width="230px" />
  );

  const tableConfig: TableConfig = {
    tableName: "Lista de Localizações",
    dataTable: dataTable,
    setDataTable: setDataTable,
    renderTable: (dataTable) => (
      <TableIndicator
        dataTable={dataTable}
        isLoaded={isLoaded}
        setValue={setValue}
        openModal={openModal}
        frequency={filterState.IndicatorId?.frequency}
      />
    ),
    isLoaded,
    errorFindingData,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize, totalItems, setIsSearching);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage: currentPage,
    totalPages: totalPages,
    handlePageChange: handlePageChange,
    renderContent: renderContent,
    setPageSize,
    totalItems: totalItems,
  };

  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "",
    "",
  );

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };

  const filterConfig: FilterConfig = {
    hasInputSearch: false,
    filterChildren,
    filters: filtersArray,
    keepActiveFilters: keepActiveFiltersArray,
    selectedFilterItem: selectedFilterItems,
  };

  const filterRules = [
    () => !filtersArray[0]?.value || !filtersArray[1]?.value,
  ];

  useEffect(() => {
    if (!isOpen) {
      resetField("StartPeriod");
      resetField("EndPeriod");
      resetField("StartYear");
      resetField("EndYear");
      setFormValues({
        frequency: null,
        province: null,
        county: null,
        indicator: null,
      });
    }
  }, [isOpen, resetField]);

  return (
    <>
      <AnalysisFilterTemplate
        title="Análise por indicadores"
        summary={INDICATOR_ANALYSIS_SUMMARY}
        filterChildren={filterChildrenMainTable}
        filters={filtersArray}
        dataTable={dataTable}
        setFilteredTable={setDataTable}
        setShowTable={setShowTable}
        setIsSearching={setIsSearching}
        filterRules={filterRules}
        setIsLoaded={setAnalysisLoaded}
      />
      {showTable &&
        (!analysisLoaded ? (
          <Box
            border={borderStyle}
            borderBottomRadius={borderRadius}
            padding="3"
          >
            {renderLoadingState()}
          </Box>
        ) : (
          <Box
            p="4"
            backgroundColor="gray.50"
            border={borderStyle}
            borderRadius={borderRadius}
            borderTopRadius="0"
          >
            <IndicatorSearchTable
              searchTableName={`Análise ${filterState.IndicatorId?.name} por região`}
              tableConfig={tableConfig}
              filterConfig={filterConfig}
              paginationConfig={paginationConfig}
              modalConfig={modalConfig}
              setIsSearching={setIsSearching}
              control={control}
              setFormValues={setFormValues}
              formValues={formValues}
            />
          </Box>
        ))}
    </>
  );
};

export default IndicatorAnalysis;
